import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../queryKeys";
import { cdFetch, cdPost, cdPostForFiles } from "../api";
import {
  IQuoteUploadPayload,
  metaDataSteelConduit,
  metaDataPvcConduit,
  IQuoteUploadStatusPayload,
  IQuoteUploadStatusResponse,
  IQuoteUploadFiltersResponse,
  ICombinedQuoteSubmitPayload,
  IAdditionalInputPayload,
  IAdditionalInputIndexPayload,
  IIndexValuesPayload,
  IDeleteQuotePayload,
} from "../../services/interfaces/IQuoteUpload";
import { useState } from "react";
import { FEATURE_FLAGS } from "../../utils/featureFlags";

/* 
  fetch filters and allow execute assessment based on roles and flag
*/

export const useQuoteUploadFilters = (userEmail: string | null) => {
  const { data, isLoading } = useQuery({
    queryKey: [queryKeys.quoteUpload, "filters", userEmail],
    queryFn: () => getQuoteUploadFilters(userEmail),
    enabled:
      userEmail &&
        userEmail !== "" &&
        userEmail !== null &&
        userEmail !== undefined
        ? true
        : false,
  });

  const filters: IQuoteUploadFiltersResponse = data?.data?.data;
  return { filters, isLoading };
};

const getQuoteUploadFilters = (userEmail: string | null) => {
  const res = cdFetch(
    `/getVendorQuoteFilter?email=${JSON.stringify(userEmail)}`,
  );
  return res;
};

/*
fetch filters for delete wire
*/

export const useQuotDeleteFilters = (
  commodity: string | undefined,
  userEmail: string | null,
  role: string | null,
  district: string | null,
  supplier: string | null,
  duration: string | null,
  quoteID: string | null,
  type: string | null,
) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      queryKeys.quoteDeleteFilter,
      "filters",
      commodity,
      userEmail,
      role,
      district,
      supplier,
      duration,
      quoteID,
      type,
    ],
    queryFn: () =>
      getQuoteDeleteFilters(
        userEmail,
        role,
        district,
        supplier,
        duration,
        quoteID,
        type,
      ),
    enabled:
      commodity === "Copper Wire" &&
        role &&
        userEmail &&
        userEmail !== "" &&
        userEmail !== null &&
        userEmail !== undefined
        ? true
        : false,
  });

  const wireFilters: any = data?.data?.data;
  return { wireFilters, isLoading, refetch };
};

const getQuoteDeleteFilters = (
  userEmail: string | null,
  role: string | null,
  district: string | null,
  supplier: string | null,
  duration: string | null,
  quoteID: string | null,
  type: string | null,
) => {
  let qParams = "";
  if (userEmail) {
    qParams += `email=${JSON.stringify(userEmail)}&`;
  }
  if (role) {
    qParams += `role=${JSON.stringify(role)}&`;
  }
  if (district) {
    qParams += `district=${JSON.stringify(district)}&`;
  }
  if (supplier) {
    qParams += `supplier=${JSON.stringify(supplier)}&`;
  }
  if (duration) {
    qParams += `contractValidity=${JSON.stringify(duration)}&`;
  }
  if (quoteID) {
    qParams += `quoteId=${JSON.stringify(quoteID)}&`;
  }
  if (type) {
    qParams += `contractType=${JSON.stringify(type)}&`;
  }
  if (qParams.length > 0) {
    qParams = "?" + qParams.slice(0, -1);
  }
  const res = cdFetch(`/getWireQuoteDeleteFilter${qParams}`);
  return res;
  return res;
};

/*
delete wire
*/

export const useQuotDelete = (
  setOpenQuoteDeleteModal: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  const [errorMessageDelete, setErrorMessageDelete] = useState<string | null>();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: [queryKeys.quoteDelete, "delete"],
    mutationFn: ({
      payload,
    }: {
      payload: IDeleteQuotePayload;
      refetchPayload: IQuoteUploadStatusPayload;
    }) => QuoteDelete(payload),
    onSuccess: (
      res,
      variables: {
        payload: IDeleteQuotePayload;
        refetchPayload: IQuoteUploadStatusPayload;
      },
    ) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.quoteUpload, "status", variables.refetchPayload], //need to invalidate on reftech according to payload
      });

      setOpenQuoteDeleteModal(false);
      setErrorMessageDelete(null);
    },
    onError: (error: any) => {
      // added any as an object as Error object has no response property
      setErrorMessageDelete(error?.response?.data?.data ?? error.message);
    },
  });
  return {
    mutate: mutation.mutate,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    isPending: mutation.isPending,
    errorMessageDelete: errorMessageDelete,
    setErrorMessageDelete: setErrorMessageDelete,
  };
};

const QuoteDelete = (payload: IDeleteQuotePayload) => {
  const res = cdPost(`/deleteWireQuote`, payload);
  return res;
};

/* 
  fetch quote upload statuses for commodities
*/

export const useQuoteUploadStatus = (
  payload: IQuoteUploadStatusPayload | undefined,
) => {
  const useDummyCopperWireStatus =
    FEATURE_FLAGS.quoteUploadCopperWireTables &&
    payload?.commodity === "Copper Wire";

  const {
    data,
    isLoading: isStatusLoading,
    refetch,
  } = useQuery({
    queryKey: [
      queryKeys.quoteUpload,
      "status",
      payload,
      useDummyCopperWireStatus ? "dummy" : "live",
    ],
    queryFn: () => getQuoteUploadStatus(payload, useDummyCopperWireStatus),
    staleTime: 20000,
    enabled: useDummyCopperWireStatus || !!payload,
  });

  const statusData = useDummyCopperWireStatus ? data?.data : data?.data?.data;

  return { statusData, isStatusLoading, refetch };
};

const getQuoteUploadStatus = (
  payload: IQuoteUploadStatusPayload | undefined,
  useDummyCopperWireStatus = false,
) => {
  let qParams = new URLSearchParams();
  if (useDummyCopperWireStatus) {
    if (payload?.quotation_date) {
      qParams.append(
        "quotation_date",
        JSON.stringify(payload?.quotation_date),
      );
    }

    if (payload?.commodity) {
      qParams.append("commodity", JSON.stringify(payload?.commodity));
      qParams.append("module", "wire");
    } else {
      qParams.append("commodity", JSON.stringify("Copper Wire"));
      qParams.append("module", "wire");
    }

    return cdFetch(`/getVendorQuoteStatus?${qParams.toString()}`);
  }

  if (
    !payload ||
    payload?.commodity?.length === 0 ||
    payload?.quotation_date?.length === 0
  )
    return;

  if (payload?.quotation_date) {
    qParams.append("quotation_date", JSON.stringify(payload?.quotation_date));
  }

  if (payload?.commodity) {
    qParams.append("commodity", JSON.stringify(payload?.commodity));
    if (payload?.commodity === "Copper Wire") {
      qParams.append("module", "wire");
    }
  }

  return cdFetch(`/getVendorQuoteStatus?${qParams.toString()}`);
};

/* 
  fetch dropdown meta data for the quote upload popup
*/

export const useQuoteUploadMetaData = (commodity: string | undefined) => {
  const { data, isLoading } = useQuery({
    queryKey: [queryKeys.quoteUpload, "meta data", commodity],
    queryFn: () => getQuoteUploadMetaData(commodity),
    enabled: !!commodity,
  });

  //   const metaData = data?.data;
  const metaData = data;
  return { metaData, isLoading };
};

const getQuoteUploadMetaData = (commodity: string | undefined) => {
  //   const res = cdFetch(`/getQuoteUploadDetails`);
  //   return res;
  if (commodity === "PVC Conduit") {
    return metaDataPvcConduit;
  }
  return metaDataSteelConduit;
};

/* 
  Submit Quote Upload Details from the popup
*/
export const useQuoteUploadSubmit = () => {
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState<any>("");
  const [errorMessage, setErrorMessage] = useState<any>();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: [queryKeys.quoteUpload, "upload file"],
    mutationFn: ({ payload }: ICombinedQuoteSubmitPayload) =>
      uploadQuoteData(payload),
    onSuccess: (res, variables: ICombinedQuoteSubmitPayload) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.quoteUpload, "upload file"],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.quoteUpload, "status", variables.refetchPayload], //need to invalidate on reftech according to payload
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.quoteUpload, "filters"],
      });
      setUploadSuccess(true);
      setSuccessMessage(res?.data?.data);
    },
    onError: (error: any) => {
      // added any as an object as Error object has no response property
      setErrorMessage(error?.response?.data?.data ?? error.message);
    },
  });

  return {
    mutate: mutation.mutate,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    uploadSuccess: uploadSuccess,
    successMessage: successMessage,
    setUploadSuccess: setUploadSuccess,
    errorMessage: errorMessage,
    setErrorMessage: setErrorMessage,
  };
};

const uploadQuoteData = async (payload: IQuoteUploadPayload) => {
  let qParams = new URLSearchParams();
  if (payload?.quoteDate) {
    qParams.append("quotation_date", JSON.stringify(payload?.quoteDate));
  }
  if (payload?.contractDate) {
    qParams.append("contractDate", JSON.stringify(payload?.contractDate));
  }
  if (payload?.user) {
    qParams.append("email", JSON.stringify(payload?.user));
  }
  if (payload?.contractType) {
    qParams.append("contractType", JSON.stringify(payload?.contractType));
  }
  if (payload?.commodity) {
    qParams.append("commodity", JSON.stringify(payload?.commodity));
    if (payload?.commodity === "Copper Wire") {
      qParams.append("module", "wire");
    } else {
      if (payload?.quoteNumber) {
        qParams.append("quotation_no", JSON.stringify(payload?.quoteNumber));
      }
      if (payload?.vendor) {
        qParams.append("vendor", JSON.stringify(payload?.vendor));
      }
    }
  }

  if (payload?.file) {
    const fileData = await payload.file.arrayBuffer();
    const res = cdPostForFiles(
      `/quotationUpload?${qParams.toString()}`,
      fileData,
    );
    return res;
  }
};

export const useExecuteAssessment = (setAssessmentExecuting: any) => {
  const [executionSuccess, setExecutionSuccess] = useState(false);
  const [errorExecutionMessage, setErrorExecutionMessage] = useState<string>();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: [queryKeys.quoteUpload, "execute assessment"],
    mutationFn: (payload: IQuoteUploadStatusPayload) =>
      executeAssessment(payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.quoteUpload, "execute assessment"],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.quoteUpload, "filters"], // refetch the filters to get the updated value for disabling the assessment for that commodity
      });
      queryClient.refetchQueries({
        queryKey: [queryKeys.screenConfig], // refetch the filters to get the updated value for disabling the assessment for that commodity
      });

      setExecutionSuccess(true);
      setAssessmentExecuting(false);
    },
    onError: (error: any) => {
      setErrorExecutionMessage(error?.response?.data?.data ?? error.message);
    },
  });

  return {
    mutate: mutation.mutate,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    executionSuccess: executionSuccess,
    setExecutionSuccess: setExecutionSuccess,
    errorExecutionMessage: errorExecutionMessage,
    setErrorExecutionMessage: setErrorExecutionMessage,
  };
};

const executeAssessment = (payload: IQuoteUploadStatusPayload) => {
  let qParams = new URLSearchParams();
  if (payload?.commodity) {
    qParams.append("commodity", JSON.stringify(payload?.commodity));
  }

  if (payload?.quotation_date) {
    qParams.append("quotation_date", JSON.stringify(payload?.quotation_date));
  }
  const res = cdPost(`/executeAssessment?${qParams.toString()}`, undefined);
  return res;
};

export const useProjectedReleaseUploadSubmit = () => {
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | []>();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: [queryKeys.projectedReleaseUpload, "upload file"],
    mutationFn: (payload: IAdditionalInputPayload) =>
      uploadProjectedRelease(payload),
    onSuccess: () => {
      setUploadSuccess(true);
    },
    onError: (error: any) => {
      // added any as an object as Error object has no response property
      setErrorMessage(error?.response?.data?.data ?? error.message);
    },
  });

  return {
    mutate: mutation.mutate,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    uploadSuccess: uploadSuccess,
    setUploadSuccess: setUploadSuccess,
    errorMessage: errorMessage,
    setErrorMessage: setErrorMessage,
  };
};

const uploadProjectedRelease = async (payload: IAdditionalInputPayload) => {
  let qParams = new URLSearchParams();
  if (payload?.email) {
    qParams.append("email", JSON.stringify(payload?.email));
  }
  if (payload?.commodity) {
    qParams.append("commodity", JSON.stringify(payload?.commodity));
  }
  if (payload?.uploadType === "uploadAgentPricing") {
    if (payload?.date) {
      const dateValue = new Date(payload?.date);
      const mm = String(dateValue.getMonth() + 1).padStart(2, "0");
      const dd = String(dateValue.getDate()).padStart(2, "0");
      const yyyy = String(dateValue.getFullYear());
      const value = `${mm}/${dd}/${yyyy}`;

      qParams.append("upload_date", JSON.stringify(value));
    }
  }
  if (payload?.uploadType === "uploadProjectedRelease") {
    if (payload?.projectedReleaseDate) {
      qParams.append("date", JSON.stringify(payload?.projectedReleaseDate));
    }
  }
  if (payload?.file) {
    const fileData = await payload.file.arrayBuffer();
    const res = cdPostForFiles(
      `/${payload?.uploadType === "uploadProjectedRelease"
        ? "uploadProjectedReleases"
        : "uploadAgentPricing"
      }?${qParams.toString()}`,
      fileData,
    );
    return res;
  }
};

export const useIndexValue = (
  commodity: string | undefined,
  email: string | null,
) => {
  const {
    data,
    isLoading: isIndexStatusLoading,
    refetch,
  } = useQuery({
    queryKey: [queryKeys.quoteUpload, "status", commodity, email],
    queryFn: () => getIndexValues(commodity, email),
    staleTime: 0,
    gcTime: 0,
    // enabled: !!(commodity?.length !== 0 || email?.length !== 0),
  });

  const indexTableData: any = data?.data?.data;

  return { indexTableData, isIndexStatusLoading, refetch };
};

const getIndexValues = (
  commodity: string | undefined,
  email: string | null,
) => {
  if (commodity?.length === 0 || email?.length === 0) return;
  let qParams = new URLSearchParams();
  if (email) {
    qParams.append("email", JSON.stringify(email));
  }

  if (commodity) {
    qParams.append("commodity", JSON.stringify(commodity));
  }

  const res = cdFetch(`/getAdditionalInputsDetails?${qParams.toString()}`);
  return res;
};

export const useIndexUploadSubmit = () => {
  // const [uploadSuccess, setUploadSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | []>();
  const [isRefetch, setIsRefetch] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: [queryKeys.indexUplaod, "upload index"],
    mutationFn: (payload: IAdditionalInputIndexPayload) => uploadIxdex(payload),
    onSuccess: () => {
      // setUploadSuccess(true);
      setIsRefetch(true);
    },
    onError: (error: any) => {
      // added any as an object as Error object has no response property
      setErrorMessage(error?.response?.data?.data ?? error.message);
    },
  });
  return {
    mutate: mutation.mutate,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    isPending: mutation.isPending,
    // uploadSuccess: uploadSuccess,
    // setUploadSuccess: setUploadSuccess,
    errorMessage: errorMessage,
    setErrorMessage: setErrorMessage,
    isRefetch: isRefetch,
    setIsRefetch: setIsRefetch,
  };
};

const uploadIxdex = async (payload: IAdditionalInputIndexPayload) => {
  const res = cdPost(`/upsertIndex`, payload);
  return res
};

/*
  fetch spots contracts for a given week type (current / previous)
*/
export const useSpotsContracts = (
  type: "current" | "previous",
  quotationDate: string | undefined,
  commodity: string | undefined,
  enabled: boolean,
) => {
  const { data, isLoading } = useQuery({
    queryKey: [queryKeys.spotsContracts, type, quotationDate, commodity],
    queryFn: () => getSpotsContracts(type, quotationDate, commodity),
    staleTime: 20000,
    enabled: enabled && !!quotationDate && !!commodity,
  });

  const spotsData = data?.data?.data ?? data?.data;
  return { spotsData, isSpotsLoading: isLoading };
};

const getSpotsContracts = (
  type: string,
  quotationDate: string | undefined,
  commodity: string | undefined,
) => {
  const qParams = new URLSearchParams();
  qParams.append("type", type);
  if (quotationDate) {
    qParams.append("quotation_date", quotationDate);
  }
  if (commodity) {
    qParams.append("commodity", commodity);
  }
  return cdFetch(`/getSpotsContracts?${qParams.toString()}`);
};
